
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface EdgeFunctionDebugProps {
  agentId: string;
  show: boolean;
}

const EdgeFunctionDebugView = ({ agentId, show }: EdgeFunctionDebugProps) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    // When component first mounts, add a log
    const timestamp = new Date().toISOString();
    setLogs(prev => [...prev, `[${timestamp}] Debug view initialized for agent: ${agentId}`]);
  }, [agentId]);
  
  const addLog = (message: string) => {
    const timestamp = new Date().toISOString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };
  
  const handleTestConnection = async () => {
    setIsLoading(true);
    addLog("Testing connection to edge function...");
    
    try {
      // Simple OPTIONS request to test CORS
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-with-agent-v2`, {
        method: "OPTIONS",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      if (response.ok) {
        addLog(`Connection successful! Status: ${response.status}`);
      } else {
        addLog(`Connection failed! Status: ${response.status} - ${response.statusText}`);
      }
    } catch (error: any) {
      addLog(`Connection error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!show) return null;
  
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm font-medium">Edge Function Debug View</CardTitle>
      </CardHeader>
      <CardContent>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This is a debug view for troubleshooting the edge function connection.
          </AlertDescription>
        </Alert>
        
        <div className="flex gap-2 mb-4">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleTestConnection}
            disabled={isLoading}
          >
            Test Connection
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setLogs([])}
          >
            Clear Logs
          </Button>
        </div>
        
        <div className="bg-muted rounded-md p-2 h-40 overflow-y-auto text-xs font-mono">
          {logs.length === 0 ? (
            <div className="text-muted-foreground p-2">No logs yet...</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} className="pb-1">
                {log}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EdgeFunctionDebugView;
